import { useFormikContext } from 'formik';
import { get, isNil } from 'lodash/fp';
import { type ReactNode, useCallback, useMemo } from 'react';
import { type FinanceProductFormValues } from '..';
import { type LicenseAndFuelTaxTableValuesPayload } from '../../../../../api/types';
import { exportFinancialTabsExcel, getWorkbookToExport } from '../../../../../utilities/excel';
import EditableTable, { type Columns } from './EditableTable';

type DisplacementSettingKey = 'licenseAndFuelTax.fuelTable' | 'licenseAndFuelTax.licenseTable';

type DisplacementValueTableProps = {
    settingsKey: DisplacementSettingKey;
    disabled: boolean;
    labelKey: ReactNode;
    valueKey: ReactNode;
};

export const useDisplacementExcelHandler = (
    settingKey: DisplacementSettingKey,
    mappedHeaders: Record<string, string>,
    exportName: string
) => {
    const { setFieldValue, values } = useFormikContext<FinanceProductFormValues>();

    const table = get(settingKey, values);
    const displayName = get('displayName', values);

    const onImport = useCallback(
        async (data: Record<string, string>[]) => {
            const updatedTable = data
                .filter(record => !isNil(record.value))
                .map(record => ({
                    min: record.min,
                    max: record.max,
                    value: record.value,
                }));

            setFieldValue(settingKey, updatedTable);
        },
        [setFieldValue, settingKey]
    );

    const onExport = useCallback(async () => {
        const workbook = await getWorkbookToExport(mappedHeaders);
        const worksheet = workbook.getWorksheet(1);

        table.forEach(record => {
            worksheet.addRow(record);
        });

        exportFinancialTabsExcel(workbook, displayName, exportName);
    }, [displayName, exportName, mappedHeaders, table]);

    return { onImport, onExport };
};

const DisplacementValueTable = ({ settingsKey, disabled, labelKey, valueKey }: DisplacementValueTableProps) => {
    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();
    const table: LicenseAndFuelTaxTableValuesPayload[] = get(settingsKey, values);

    const onUpdate = useCallback(
        (newRecord: LicenseAndFuelTaxTableValuesPayload) => {
            const newTable = table.map(record => {
                if (record.min === newRecord.min && record.max === newRecord.max) {
                    return { ...record, value: newRecord.value };
                }

                return record;
            });

            setFieldValue(settingsKey, newTable);
        },
        [setFieldValue, settingsKey, table]
    );

    const columns = useMemo<Columns<LicenseAndFuelTaxTableValuesPayload>>(
        () => [
            {
                title: labelKey,
                dataIndex: 'displacement',
                fixed: true,
                render: (value, record: LicenseAndFuelTaxTableValuesPayload) =>
                    record.min === record.max ? `${record.min}` : `${record.min} - ${record.max}`,
            },
            {
                title: valueKey,
                dataIndex: 'value',
                onCell: record => ({ record, dataIndex: 'value', isEditable: !disabled, onUpdate }),
            },
        ],
        [onUpdate, disabled, labelKey, valueKey]
    );

    return <EditableTable columns={columns} records={table} />;
};

export default DisplacementValueTable;
